import { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { updatePassword } from 'aws-amplify/auth';

import { usePromiseStatus } from 'hooks/usePromiseStatus/usePromiseStatus';

import { CognitoExceptions } from 'interfaces/auth';
import { IModalProps as Props } from 'interfaces/modalProps';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { PasswordTextInputControlled } from 'components/_basic/Form/PasswordTextInput';
import { ModalComponent } from 'components/_basic/ModalComponent/ModalComponent';

import { changePasswordValidationSchema } from 'helpers/validations/changePasswordFormValidation';

import { IChangePasswordForm } from './types';

import styles from './ChangePasswordForm.module.scss';

export const ChangePasswordForm: FC<Props> = ({ isOpened, handleClose }) => {
  const { t } = useTranslation();
  const { track, isPending } = usePromiseStatus();

  const changePasswordErrorsMap = new Map<string, string>([
    [CognitoExceptions.NotAuthorizedException, t('wrongPassword')],
  ]);

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
    clearErrors,
    reset: resetForm,
  } = useForm<IChangePasswordForm>({
    resolver: yupResolver<IChangePasswordForm>(changePasswordValidationSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const onSubmit: SubmitHandler<IChangePasswordForm> = async ({ password, newPassword }): Promise<void> => {
    try {
      await track(updatePassword({ oldPassword: password, newPassword: newPassword }));

      clearErrors('password');
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        setError(
          'password',
          { message: changePasswordErrorsMap.get(error.name) ?? error.message, type: error.name },
          { shouldFocus: true },
        );
      }
    }
  };

  const handleModalClose = useCallback((): void => {
    resetForm();
    handleClose();
  }, [resetForm, handleClose]);

  return (
    <ModalComponent open={isOpened} onClose={handleModalClose}>
      <Box className={styles.changePasswordFormWrapper}>
        <Box className={styles.formContent}>
          <Grid container alignItems="center">
            <Grid item xs={11}>
              <Typography variant="h1" className={styles.formTitle}>
                {t('changePassword')}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <CloseButton onClick={handleModalClose} />
            </Grid>
          </Grid>
          <Typography variant='body2'>{t('changePasswordFormDescription')}</Typography>
          <Box className={styles.formContent} component="form" onSubmit={handleSubmit(onSubmit)}>
            <PasswordTextInputControlled
              name="password"
              label={t('currentPassword')}
              placeholder={t('enterCurrentPassword')}
              control={control}
            />
            <PasswordTextInputControlled
              name="newPassword"
              label={t('newPassword')}
              placeholder={t('enterNewPassword')}
              control={control}
            />
            <PasswordTextInputControlled
              name="confirmNewPassword"
              label={t('confirmNewPassword')}
              placeholder={t('confirmNewPassword')}
              control={control}
            />
            <Button
              fullWidth
              isLoading={isPending}
              disabled={Boolean(!isValid) || isPending}
              variant="contained"
              type="submit"
            >
              {t('saveNewPassword')}
            </Button>
          </Box>
        </Box>
      </Box>
    </ModalComponent>
  );
};
